import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService, SignupRequest, SigninRequest, ConfirmRequest, PasswordResetConfirmRequest } from '../../../services/authService';
import { useAuthStore } from '../../../stores/authStore';
import { showToast } from '../../../stores/uiStore';
import { handleApiError } from '../../../services/api';
import { ROUTES } from '../../../config/routes';
import { getOperationErrorMessage } from '../../../config/errorMessages';

export const useSignup = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: SignupRequest) => authService.signup(data),
    onSuccess: (response, variables) => {
      if (response.success) {
        showToast.success('確認メールを送信しました。メールをご確認ください。');
        navigate(ROUTES.CONFIRM_EMAIL, { state: { email: variables.email } });
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('create'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useConfirmSignup = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (data: ConfirmRequest) => authService.confirm(data),
    onSuccess: async (response, variables) => {
      if (response.success) {
        const storedPw = sessionStorage.getItem('_eb_auto');
        sessionStorage.removeItem('_eb_auto');
        if (storedPw) {
          try {
            const signinRes = await authService.signin({ email: variables.email, password: storedPw });
            if (signinRes.success && signinRes.data) {
              setAuth(signinRes.data.user, signinRes.data.tokens);
              showToast.success('アカウントが作成されました');
              navigate(ROUTES.DASHBOARD);
              return;
            }
          } catch {
            // autoSignIn失敗時はログイン画面へフォールバック
          }
          navigate(ROUTES.LOGIN);
          showToast.success('メール認証が完了しました。ログインしてください。');
        } else {
          showToast.success('メール認証が完了しました。ログインしてください。');
          navigate(ROUTES.LOGIN);
        }
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('auth'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useResendConfirmation = () => {
  return useMutation({
    mutationFn: (email: string) => authService.resendConfirmation(email),
    onSuccess: (response) => {
      if (response.success) {
        showToast.success('確認メールを再送信しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('auth'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useSignin = () => {
  return useMutation({
    mutationFn: (data: SigninRequest) => authService.signin(data),
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear();
      clearAuth();
      showToast.success('ログアウトしました');
      navigate(ROUTES.LOGIN);
    },
    onError: () => {
      // エラーがあってもログアウト処理は続行
      queryClient.clear();
      clearAuth();
      navigate(ROUTES.LOGIN);
    },
  });
};

export const useRequestPasswordReset = () => {
  return useMutation({
    mutationFn: (email: string) => authService.requestPasswordReset(email),
    onSuccess: (response) => {
      if (response.success) {
        showToast.success('パスワードリセット用のメールを送信しました');
      } else {
        // セキュリティのため、エラーでも同じメッセージ
        showToast.success('メールアドレスが登録されている場合、リセット用のメールを送信しました');
      }
    },
    onError: () => {
      // セキュリティのため、エラーでも同じメッセージ
      showToast.success('メールアドレスが登録されている場合、リセット用のメールを送信しました');
    },
  });
};

export const useConfirmPasswordReset = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: PasswordResetConfirmRequest) => authService.confirmPasswordReset(data),
    onSuccess: (response) => {
      if (response.success) {
        showToast.success('パスワードをリセットしました');
        navigate(ROUTES.LOGIN);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('auth'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(currentPassword, newPassword),
    onSuccess: (response) => {
      if (response.success) {
        showToast.success('パスワードを変更しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('auth'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};
